// Lo que decide la pantalla en las filas del Registro Público de Comercio del Explorador (etapa 4, «Nexo adopta las piezas»), sin
// React, para probarlo con Deno (app/scripts/nexo-ficha.test.ts). La relación de cada fila, su texto y su tono los decide la base
// (nexo.buscar_sujetos, alineado con nexo.hallazgos en el lote 0b): aquí no hay reglas propias de color ni de identidad.
import type { Ruta } from "../ruta.ts";

/** Una fila de la búsqueda común de Nexo y Argus Team (nexo.buscar_sujetos, 2026-10-01): la base dice la relación con OFAC. */
export interface SujetoBusqueda {
  /** `sat_rep` (corte 4a-2 de los certificados del SAT): un representante sin ficha confirmada, sólo si se pidió (`satRep`). */
  kind: "entity" | "siger_actor" | "sat_rep";
  id: string;
  runId: string | null;
  caption: string;
  schema: string;
  /** `posible` (20261017200000): «OFAC lo sacó de la lista» de algo sólo posible; la base lo manda sólo cuando lo es. */
  relacion: { clave: string; texto: string; con?: string | null; tono?: "red" | "amber" | "gray" | null; posible?: boolean } | null;
  /**
   * Identidades con alguien de la lista. `corroborada` (lote 0b, 2026-10-04) es la regla de la ficha (nexo.identidad_corroborada);
   * una base anterior no la manda.
   */
  ownOfacCandidates: { entityId: string; nivel: string; estado: string | null; corroborada?: boolean | null }[];
  /** `posible` (lote 0b): la persona o empresa del acta puede no ser la de la lista (ancla sin corroborar). */
  siger: { expedientes?: number; linkCount?: number; links?: { entityId: string; caption: string; tipo?: string; tipoTexto?: string; posible?: boolean | null }[] } | null;
  group: { count: number } | null;
  /**
   * Corte 2c de los certificados del SAT: todas las relaciones de la fila, hasta 5, en el orden de la principal (la base las ordena y
   * marca la principal); `total`, cuántas son. Una base anterior no la manda: la fila pinta `siger.links`, como antes.
   */
  relaciones?: { total: number; items: RelacionFila[] } | null;
}
/** Una relación de la lista de la fila: de un acta o de un certificado del SAT (con su año), terminada (`vigente: false`) o posible. */
export interface RelacionFila {
  clave: string; texto: string; con?: string | null; entidad?: string | null; tono?: "red" | "amber" | "gray" | null;
  fuente: "acta" | "certificado_sat"; anio?: string | null; vigente?: boolean | null; posible?: boolean | null; principal?: boolean | null;
}
/** Cuántas relaciones de la fila no caben (están en la ficha). */
export const relacionesQueNoCaben = (a: Pick<SujetoBusqueda, "relaciones">): number =>
  a.relaciones ? Math.max(0, a.relaciones.total - a.relaciones.items.length) : 0;

/** Tono de la fila: sólo el que manda la base (`relacion.tono`). Rojo sólo si la base lo dice, y la base sólo lo dice con identidad o
 * relación corroboradas (lote 0b). Sin tono, gris. */
export type TonoFila = "rojo" | "ambar" | "gris";
export function tonoDeFila(a: Pick<SujetoBusqueda, "relacion">): TonoFila {
  const t = a.relacion?.tono;
  return t === "red" ? "rojo" : t === "amber" ? "ambar" : "gris";
}

/**
 * La identidad de la fila con alguien de la lista, sólo si la base la corrobora (nexo.identidad_corroborada, la regla de la ficha).
 * Antes (Explorador.tsx:383) bastaba el nivel `confirmada` o `alta`, y una `alta` sin corroborar abría la ficha del sancionado como si
 * fuera la misma persona (pendiente del lote 0b). Sin el campo (base anterior al lote 0b), ninguna cuenta: no se adivina.
 */
export function identidadCorroborada(a: Pick<SujetoBusqueda, "ownOfacCandidates">): string | null {
  return a.ownOfacCandidates.find((o) => o.corroborada === true && o.estado !== "rejected")?.entityId ?? null;
}

/**
 * A dónde lleva el nombre de una fila:
 * - la ficha nueva de la persona o empresa del registro (nexo.hallazgos con `siger_actor`, su corrida), que dice qué afirma Argus de
 *   ella con las piezas compartidas, igual que Argus Team;
 * - sin corrida (no debería pasar con una fila del registro), la ficha de su identidad corroborada en la lista o la del sancionado de su
 *   primera relación confirmada, con la persona elegida; nunca la de una identidad o relación sin confirmar;
 * - si no hay a dónde ir, null (el nombre no es enlace).
 */
export function destinoDeFila(a: Pick<SujetoBusqueda, "kind" | "id" | "runId" | "ownOfacCandidates" | "siger">): Ruta | null {
  if (a.kind === "entity") return { vista: "entidad", id: a.id };
  if (a.kind === "sat_rep") return { vista: "representante", id: a.id };
  if (a.runId) return { vista: "registro", id: a.id, runId: a.runId };
  const propia = identidadCorroborada(a);
  if (propia) return { vista: "entidad", id: propia, actor: a.id };
  // Confirmada sólo si la base lo dice (`posible: false`), como `corroborada`: sin el campo no se adivina.
  const confirmada = (a.siger?.links ?? []).find((l) => l.posible === false);
  return confirmada ? { vista: "entidad", id: confirmada.entityId, actor: a.id } : null;
}

/**
 * Marca «posible» de una relación de la lista bajo cada fila (`siger.links`, decisión de Bernardo del 2026-10-04): la relación se
 * sigue listando aunque el ancla del acta no esté corroborada, con esta marca. Corte 4c: los textos aprobados viven en
 * app/shared/rotulos/buscador.ts (antes, una copia vigilada de los de Argus Team); aquí sólo se reexportan.
 */
export { MARCA_POSIBLE, MARCA_POSIBLE_AYUDA } from "../../../shared/rotulos/buscador.ts";

export const esPosible = (l: { posible?: boolean | null }): boolean => l.posible === true;
